import axios from 'axios';

import type { IApiResponse, IAccessTokenResponse } from './types';
import { getAccessToken, setAccessToken, clearAccessToken } from './token';
import { isNoMembershipError } from '@/features/auth/utils/auth-errors';

declare module 'axios' {
    interface InternalAxiosRequestConfig {
        _retry?: boolean;
    }
}

/**
 * Exported for the one place the frontend must build an absolute API URL:
 * the OAuth sign-in `/start` link is a full-page navigation to the API, not
 * an `apiClient` request, so `baseURL` cannot be applied for it.
 */
export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

const PUBLIC_PATHS = [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    // Both carry state we must not destroy on a failed refresh: the invite
    // token lives in the accept URL, and /no-workspace is itself where a
    // session-less user belongs.
    '/accept-invitation',
    '/no-workspace',
    // The OAuth callback carries a single-use ticket in its URL fragment; a
    // failed refresh (stale token from an earlier session) must not redirect
    // away before it is exchanged.
    '/auth/callback',
    '/',
];

/** Where a session-less / workspace-less user is sent. */
const NO_WORKSPACE_PATH = '/no-workspace';

export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
});

// ---------------------------------------------------------------------------
// Request interceptor — attach access token
// ---------------------------------------------------------------------------
apiClient.interceptors.request.use(
    (config) => {
        const token = getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error: unknown) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor — refresh on 401 with concurrent request queuing
// ---------------------------------------------------------------------------
interface IQueueItem {
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
}

let isRefreshing = false;
let failedQueue: IQueueItem[] = [];

function processQueue(error: unknown, token: string | null): void {
    for (const item of failedQueue) {
        if (token) {
            item.resolve(token);
        } else {
            item.reject(error);
        }
    }
    failedQueue = [];
}

function isPublicPath(pathname: string): boolean {
    return PUBLIC_PATHS.some((path) => {
        if (path === '/') return pathname === '/';
        return pathname.startsWith(path);
    });
}

apiClient.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
        if (!axios.isAxiosError(error) || !error.config) {
            return Promise.reject(error);
        }

        const originalRequest = error.config;
        const status = error.response?.status;

        // Access revoked mid-session (removed from the organization). Refreshing
        // cannot help — the backend rejects refresh for a dead membership too —
        // so end the session immediately instead of leaving the user in a UI
        // where every request fails. Scoped to the AUTH_NO_MEMBERSHIP code, so
        // an ordinary permission denial (also 403) does not log anyone out.
        if (status === 403 && isNoMembershipError(error)) {
            clearAccessToken();
            if (!isPublicPath(window.location.pathname)) {
                window.location.replace(`${NO_WORKSPACE_PATH}?reason=removed`);
            }
            return Promise.reject(error);
        }

        // Don't attempt refresh for non-401, already-retried, or auth endpoints
        const isAuthEndpoint = originalRequest.url?.startsWith('/auth/');
        if (status !== 401 || originalRequest._retry || isAuthEndpoint) {
            return Promise.reject(error);
        }

        // Queue this request if a refresh is already in progress
        if (isRefreshing) {
            return new Promise<string>((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return apiClient(originalRequest);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const { data } = await axios.post<IApiResponse<IAccessTokenResponse>>(
                `${API_BASE_URL}/auth/refresh`,
                undefined,
                { withCredentials: true },
            );

            setAccessToken(data.data.accessToken);
            processQueue(null, data.data.accessToken);

            originalRequest.headers.Authorization = `Bearer ${data.data.accessToken}`;
            return apiClient(originalRequest);
        } catch (refreshError: unknown) {
            clearAccessToken();
            processQueue(refreshError, null);

            if (!isPublicPath(window.location.pathname)) {
                window.location.replace('/login');
            }

            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    },
);
